import {hexToAscii} from '@Utils/string.util';
import {integerToRoman} from '@Utils/math.util';
import {LIST_TYPES, ORDERED_LIST_STYLE, UNORDERED_BULLETS_ASCII_MAP, UNORDERED_LIST_STYLE} from '@PosterWhiteboard/items/text-item/text-list';
import type {LIST_STYLE} from '@PosterWhiteboard/items/text-item/text-list';
import type {TextLineListObject, TextLineStylesObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';

const TYPED_UNORDERED_PREFIXES = ['- ', '* '];
const TYPED_ORDERED_PREFIX_PATTERN = /^\d+\. $/;

const TOTAL_ALPHA_COUNT = 26;

/**
 * The marker string a list line renders (a bullet glyph from the bullets font for unordered
 * styles, a styled ordinal plus a period for ordered ones).
 */
export const getListMarkerText = (list: TextLineListObject, ordinal: number): string => {
  if (list.type === LIST_TYPES.UNORDERED) {
    return hexToAscii(UNORDERED_BULLETS_ASCII_MAP.get(list.style) ?? '');
  }
  return `${getOrderedListMarkerLabel(list.style as ORDERED_LIST_STYLE, ordinal)}.`;
};

/**
 * Ordinals for the ordered list lines: numbering runs through consecutive ordered lines OF THE
 * SAME STYLE and restarts after any break — a non-list row, an unordered row, or a style change
 * ("1." followed by "02." reads as a numbering glitch, so a new style starts a new list),
 * matching standard editors.
 */
export const computeLineListOrdinals = (lineStyles: Record<string, TextLineStylesObject>): Record<string, number> => {
  const orderedLineIndexes = Object.keys(lineStyles)
    .filter((lineIndex) => lineStyles[lineIndex].list?.type === LIST_TYPES.ORDERED)
    .map(Number)
    .sort((a, b) => a - b);
  const ordinals: Record<string, number> = {};
  let previousLineIndex: number | null = null;
  let previousStyle: LIST_STYLE | null = null;
  let ordinal = 0;
  for (const lineIndex of orderedLineIndexes) {
    const style = lineStyles[lineIndex].list?.style ?? null;
    const continuesSequence = previousLineIndex !== null && lineIndex === previousLineIndex + 1 && style === previousStyle;
    ordinal = continuesSequence ? ordinal + 1 : 1;
    ordinals[lineIndex] = ordinal;
    previousLineIndex = lineIndex;
    previousStyle = style;
  }
  return ordinals;
};

const getOrderedListMarkerLabel = (style: ORDERED_LIST_STYLE, ordinal: number): string => {
  switch (style) {
    case ORDERED_LIST_STYLE.NUMBERED_DECIMAL:
      return ordinal.toString();
    case ORDERED_LIST_STYLE.NUMBERED:
      return ordinal < 10 ? `0${ordinal}` : ordinal.toString();
    case ORDERED_LIST_STYLE.ALPHA_UPPER:
      return getAlphaMarkerLabel(ordinal, '@');
    case ORDERED_LIST_STYLE.ALPHA_LOWER:
      return getAlphaMarkerLabel(ordinal, '`');
    case ORDERED_LIST_STYLE.ROMAN_UPPER:
      return integerToRoman(ordinal);
    case ORDERED_LIST_STYLE.ROMAN_LOWER:
      return integerToRoman(ordinal).toLowerCase();
    default:
      return ordinal.toString();
  }
};

/** A, B … Z, AA, BB … (the legacy bullets' alphabetic ordinal scheme). */
const getAlphaMarkerLabel = (ordinal: number, baseChar: '@' | '`'): string => {
  if (ordinal % TOTAL_ALPHA_COUNT !== 0) {
    const letter = String.fromCharCode(baseChar.charCodeAt(0) + (ordinal % TOTAL_ALPHA_COUNT));
    return Math.floor(ordinal / TOTAL_ALPHA_COUNT) > 0 ? letter.repeat(Math.floor(ordinal / TOTAL_ALPHA_COUNT) + 1) : letter;
  }
  return String.fromCharCode(baseChar.charCodeAt(0) + TOTAL_ALPHA_COUNT).repeat(Math.floor(ordinal / TOTAL_ALPHA_COUNT));
};

/** The default list a fresh row gets: the standard style per type; the marker inherits the text color. */
export const getDefaultLineList = (type: LIST_TYPES.ORDERED | LIST_TYPES.UNORDERED): TextLineListObject => {
  return {
    type,
    style: type === LIST_TYPES.UNORDERED ? UNORDERED_LIST_STYLE.CIRCLE_1 : ORDERED_LIST_STYLE.NUMBERED_DECIMAL,
    // null = the marker inherits its line's text color at render time
    fill: null,
    // null = the fork sizes the gutter live; only migrated legacy rows carry an explicit one
    indent: null,
  };
};

/** The list type a typed line prefix triggers ("- ", "* ", "1. " …), or null. */
export const getListTypeForTypedPrefix = (typedPrefix: string): LIST_TYPES.ORDERED | LIST_TYPES.UNORDERED | null => {
  if (TYPED_UNORDERED_PREFIXES.includes(typedPrefix)) {
    return LIST_TYPES.UNORDERED;
  }
  if (TYPED_ORDERED_PREFIX_PATTERN.test(typedPrefix)) {
    return LIST_TYPES.ORDERED;
  }
  return null;
};

/**
 * The single list shared by every line of the text, or null when lines differ (or no line has
 * one). Pure so reactive callers (hooks under the React Compiler) can derive it from Redux data —
 * an imperative read of the item class gets memoized against the stable class reference and goes
 * stale (the list sidebar bug of 2026-09-09).
 */
export const getUniformLineList = (lineStyles: Record<number, TextLineStylesObject> | undefined, text: string | undefined): TextLineListObject | null => {
  if (lineStyles === undefined || text === undefined) {
    return null;
  }
  const lineCount = text.split('\n').length;
  const firstList = lineStyles[0]?.list ?? null;
  if (firstList === null) {
    return null;
  }
  for (let lineIndex = 1; lineIndex < lineCount; lineIndex++) {
    const list = lineStyles[lineIndex]?.list;
    if (list?.type !== firstList.type || list.style !== firstList.style) {
      return null;
    }
  }
  return firstList;
};
